/**
 * AuditLogModal — visar revisionshistorik för en specifik arbetsdag.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  Plus,
  PencilLine,
  Sparkle,
  Lock,
  LockOpen,
  ArrowsClockwise,
  Clock,
  Trash,
} from "@phosphor-icons/react";
import ModalPortal from "@/components/ModalPortal";

const ACTION_META = {
  created: { icon: Plus, color: "text-emerald-700", bg: "bg-emerald-50", label: "Skapad" },
  edited: { icon: PencilLine, color: "text-amber-700", bg: "bg-amber-50", label: "Rättad" },
  stamp_out: { icon: Clock, color: "text-brand", bg: "bg-brand/5", label: "Utstämpling" },
  marked_done: { icon: Sparkle, color: "text-blue-700", bg: "bg-blue-50", label: "Klar-markerad" },
  unmarked_done: { icon: ArrowsClockwise, color: "text-text-muted", bg: "bg-app", label: "Klar-markering hävd" },
  approved: { icon: Lock, color: "text-emerald-700", bg: "bg-emerald-50", label: "Godkänd" },
  unapproved: { icon: LockOpen, color: "text-amber-700", bg: "bg-amber-50", label: "Attest hävd" },
  deleted: { icon: Trash, color: "text-red-700", bg: "bg-red-50", label: "Raderad" },
  auto_close_reports: { icon: ArrowsClockwise, color: "text-text-muted", bg: "bg-app", label: "Auto-stängning" },
};

function fmtDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 19).replace("T", " ");
  // Lokal svensk tid (Europe/Stockholm), "YYYY-MM-DD HH:mm:ss"
  const parts = new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Europe/Stockholm",
  }).formatToParts(d);
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${m.year}-${m.month}-${m.day} ${m.hour}:${m.minute}:${m.second}`;
}

function fmtFieldValue(v) {
  if (v == null) return "—";
  if (typeof v === "string" && v.match(/^\d{4}-\d{2}-\d{2}T/)) {
    // HH:MM i svensk tid för tidsstämplar
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString("sv-SE", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Stockholm",
      });
    }
    return v.slice(11, 16);
  }
  if (typeof v === "number") return String(v);
  return String(v);
}

const FIELD_LABELS = {
  stamp_in: "Stämplat in",
  stamp_out: "Stämplat ut",
  workplace_id: "Arbetsplats-ID",
  workplace_name: "Arbetsplats",
  note: "Notering",
  gross_minutes: "Brutto (min)",
  deducted_minutes: "Avdrag rast (min)",
  net_minutes: "Netto (min)",
};

export function AuditLogModal({ workday, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/workday/audit-log?workday_id=${workday.workday_id}`);
        setEntries(r.data.entries || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Kunde inte hämta revisionslogg");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workday.workday_id]);

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="audit-log-modal"
      >
        <div
          className="bg-surface border border-app rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between shrink-0">
            <div>
              <h2 className="font-heading text-2xl">Revisionslogg</h2>
              <p className="text-sm text-text-muted mt-1">
                {workday.user_name} · {(workday.stamp_in || "").slice(0, 10)} ·{" "}
                {workday.workplace_name || "Ingen arbetsplats"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary"
              aria-label="Stäng"
              data-testid="audit-modal-close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="mt-5 overflow-y-auto flex-1 -mx-2 px-2">
            {loading ? (
              <div className="text-center py-10 text-text-muted">
                Hämtar historik...
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-10 text-text-muted">
                Ingen historik hittades.
              </div>
            ) : (
              <ol className="space-y-3">
                {entries.map((e) => {
                  const meta = ACTION_META[e.action] || {
                    icon: Clock,
                    color: "text-text-muted",
                    bg: "bg-app",
                    label: e.action,
                  };
                  const Icon = meta.icon;
                  return (
                    <li
                      key={e.entry_id}
                      className="flex gap-3 border-b border-app/50 pb-3 last:border-0 last:pb-0"
                    >
                      <div
                        className={`shrink-0 w-9 h-9 rounded-full ${meta.bg} ${meta.color} flex items-center justify-center`}
                      >
                        <Icon size={16} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="font-medium">{meta.label}</div>
                          <div className="text-xs text-text-muted tabular-nums">
                            {fmtDT(e.at)}
                          </div>
                        </div>
                        <div className="text-xs text-text-muted mt-0.5">
                          av <strong>{e.actor_name}</strong> ({e.actor_role})
                        </div>
                        {e.changes && Object.keys(e.changes).length > 0 && (
                          <ul className="mt-2 text-xs bg-app/50 border border-app rounded-md p-2 space-y-1">
                            {Object.entries(e.changes).map(([field, chg]) => (
                              <li key={field} className="flex items-baseline gap-2">
                                <span className="text-text-muted min-w-[110px]">
                                  {FIELD_LABELS[field] || field}:
                                </span>
                                <span className="line-through text-text-muted">
                                  {fmtFieldValue(chg.from)}
                                </span>
                                <span className="text-text-muted">→</span>
                                <span className="font-medium">
                                  {fmtFieldValue(chg.to)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {e.note && (
                          <div className="mt-1.5 text-xs text-text-muted italic">
                            {e.note}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
